import { useState } from 'react';
import {
  Warning,
  Check,
  CopySimple,
  Desktop,
  Eye,
  EyeClosed,
  Key,
  Plus,
  ShieldCheck,
  Sparkle,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import { useLicenses, type NeuraxLicense } from '@/lib/licenses/useLicenses';
import { useAuth } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/utils/cn';
import {
  IssueDemoLauncher,
  type LaunchedDemoConfig,
} from '@/components/licenses/IssueDemoLauncher';
import { ScheduleSessionWizard } from '@/components/sessions/ScheduleSessionWizard';

export default function LicensesPage() {
  const { isAdmin } = useAuth();
  const { data, isLoading, isError, error } = useLicenses();
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [demoWizardConfig, setDemoWizardConfig] = useState<LaunchedDemoConfig | null>(null);

  return (
    <>
      <PageHeader
        eyebrow="NeuraX"
        title="License keys"
        description="Issue, monitor, and revoke NeuraX licenses for your candidates."
        actions={
          isAdmin ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="issue-demo-btn"
                onClick={() => setLauncherOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-2.5 text-sm font-semibold text-orange-300 transition hover:bg-orange-500/20"
              >
                <Sparkle className="h-4 w-4" />
                Issue demo
              </button>
              <button
                data-testid="issue-license-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
              >
                <Plus className="h-4 w-4" />
                Issue license
              </button>
            </div>
          ) : null
        }
      />

      <IssueDemoLauncher
        open={launcherOpen}
        onClose={() => setLauncherOpen(false)}
        onLaunch={(cfg) => {
          setLauncherOpen(false);
          setDemoWizardConfig(cfg);
        }}
      />
      {demoWizardConfig && (
        <ScheduleSessionWizard
          open={true}
          onClose={() => setDemoWizardConfig(null)}
          sessionType="interview"
          defaultEndClientId={demoWizardConfig.endClientId}
          demoMode={demoWizardConfig.demoMode}
        />
      )}

      {isError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <Warning className="mt-0.5 h-4 w-4" />
          <span>{(error as Error)?.message || 'Failed to load licenses.'}</span>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} data-testid="license-skeleton" className="glass-card h-24 animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && (data?.length ?? 0) === 0 && (
        <div data-testid="licenses-empty">
          <EmptyState
            title="No licenses issued yet"
            description="Issue your first NeuraX license to a candidate to get started."
          />
        </div>
      )}

      {!isLoading && (data?.length ?? 0) > 0 && (
        <div className="space-y-3">
          {data!.map((lic) => (
            <LicenseRow key={lic.id} lic={lic} />
          ))}
        </div>
      )}
    </>
  );
}

function LicenseRow({ lic }: { lic: NeuraxLicense }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(lic.license_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  const isActive = lic.status === 'active';
  const isDemo = lic.license_type === 'employee_demo';

  // Demo licenses are bound to an employee, not an end client
  const recipientName = isDemo
    ? lic.assigned_to_employee?.full_name || lic.assigned_to_employee?.email || 'Unassigned demo'
    : lic.end_client?.name || 'Unassigned candidate';
  const recipientEmail = isDemo
    ? lic.assigned_to_employee?.email || '—'
    : lic.end_client?.email || '—';

  return (
    <div
      data-testid={`license-row-${lic.id}`}
      className={cn('glass-card p-5', isDemo && 'border-orange-500/20')}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'grid h-10 w-10 place-items-center rounded-xl',
              isDemo
                ? 'bg-gradient-to-br from-orange-500/30 to-orange-600/30 text-orange-400'
                : 'bg-gradient-to-br from-neurax-electric/30 to-neurax-plasma/30 text-neurax-electric'
            )}
          >
            {isDemo ? <Sparkle weight="fill" className="h-4 w-4" /> : <Key className="h-4 w-4" />}
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{recipientName}</p>
            <p className="text-xs text-muted-foreground">{recipientEmail}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isDemo && (
            <span className="inline-flex items-center rounded-full border border-orange-500/30 bg-orange-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-orange-400">
              DEMO
            </span>
          )}
          <StatusBadge status={lic.status} />
          {lic.machine_id ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <Desktop className="h-3 w-3" />
              Bound
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.02] px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Unbound
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-white/5 bg-black/20 px-3 py-2.5">
        <ShieldCheck
          className={cn('h-4 w-4', isActive ? 'text-neurax-mint' : 'text-muted-foreground')}
        />
        <code className="font-mono text-xs tracking-wide text-white">
          {revealed ? lic.license_key : maskKey(lic.license_key)}
        </code>
        <div className="ml-auto flex items-center gap-1">
          <button
            data-testid={`reveal-key-${lic.id}`}
            onClick={() => setRevealed((v) => !v)}
            className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white"
            aria-label={revealed ? 'Hide key' : 'Reveal key'}
          >
            {revealed ? <EyeClosed className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
          <button
            data-testid={`copy-key-${lic.id}`}
            onClick={copy}
            className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white"
            aria-label="Copy key"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-neurax-mint" />
            ) : (
              <CopySimple className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Meta label="Issued" value={fmt(lic.created_at)} />
        <Meta label="Last used" value={lic.last_used_at ? fmt(lic.last_used_at) : '—'} />
        <Meta
          label="Valid until"
          value={lic.valid_until ? fmtDate(lic.valid_until) : 'Open-ended'}
        />
        <Meta label="Type" value={lic.license_type.toUpperCase()} />
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 truncate text-white">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  const label = s.charAt(0).toUpperCase() + s.slice(1);
  if (s === 'active') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-neurax-mint/30 bg-neurax-mint/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-mint">
        <span className="h-1.5 w-1.5 rounded-full bg-neurax-mint shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
  );
}

function maskKey(k: string) {
  if (k.length <= 8) return '•'.repeat(k.length);
  return `${k.slice(0, 4)}••••${k.slice(-4)}`;
}

function fmt(s: string) {
  return new Date(s).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
